import { Program, UpsertTranslationDto } from "../../api";

export const SUPPORTED_ONBOARDING_CUSTOMER_LOCALES = ["en", "es", "de", "fr", "it", "bg", "ca", "hu"] as const;

export type OnboardingCustomerLocale = typeof SUPPORTED_ONBOARDING_CUSTOMER_LOCALES[number];
export type OnboardingRewardNameKey = "welcome" | "loyalty" | "exclusive";

export interface OnboardingRewardCopyInput {
  nameKey: OnboardingRewardNameKey;
  value: number;
  redeemCost: number;
  currency: string;
  locale: string;
}

export interface OnboardingRewardCopy {
  title: string;
  description: string;
}

export interface OnboardingEarningFlowCopy {
  title: string;
  description: string;
  longDescription: string;
}

export interface OnboardingLocationCampaignCopy {
  name: string;
  body: string;
}

export interface OnboardingProgramLocales {
  defaultLocale: OnboardingCustomerLocale;
  enabledLocales: OnboardingCustomerLocale[];
}

export interface CreatedOnboardingRewardForTranslations {
  rewardId: string;
  couponId: string;
  nameKey: OnboardingRewardNameKey;
  value: number;
  redeemCost: number;
}

export interface CreatedOnboardingEntitiesForTranslations {
  program: Program;
  programEngagementFlowId?: string;
  rewards: CreatedOnboardingRewardForTranslations[];
  campaignIds: string[];
}

const REWARD_TITLES: Record<OnboardingCustomerLocale, Record<OnboardingRewardNameKey, string>> = {
  en: {
    welcome: "Welcome Reward",
    loyalty: "Loyalty Reward",
    exclusive: "Exclusive Reward",
  },
  es: {
    welcome: "Recompensa de bienvenida",
    loyalty: "Recompensa de fidelidad",
    exclusive: "Recompensa exclusiva",
  },
  de: {
    welcome: "Willkommensbelohnung",
    loyalty: "Treuebelohnung",
    exclusive: "Exklusive Belohnung",
  },
  fr: {
    welcome: "Récompense de bienvenue",
    loyalty: "Récompense de fidélité",
    exclusive: "Récompense exclusive",
  },
  it: {
    welcome: "Premio di benvenuto",
    loyalty: "Premio fedeltà",
    exclusive: "Premio esclusivo",
  },
  bg: {
    welcome: "Награда за добре дошли",
    loyalty: "Награда за лоялност",
    exclusive: "Ексклузивна награда",
  },
  ca: {
    welcome: "Recompensa de benvinguda",
    loyalty: "Recompensa de fidelització",
    exclusive: "Recompensa exclusiva",
  },
  hu: {
    welcome: "Üdvözlő jutalom",
    loyalty: "Hűségjutalom",
    exclusive: "Exkluzív jutalom",
  },
};

const EARNING_FLOW_COPY: Record<OnboardingCustomerLocale, OnboardingEarningFlowCopy> = {
  en: {
    title: "Earn points on every purchase",
    description: "Get points every time you shop with us, online or in store.",
    longDescription: "Every order earns you points, whether you shop online or in store. The more you shop, the faster you reach your next reward.",
  },
  es: {
    title: "Gana puntos en cada compra",
    description: "Consigue puntos cada vez que compres con nosotros, online o en tienda.",
    longDescription: "Cada pedido te da puntos, compres online o en tienda. Cuanto más compres, antes llegarás a tu próxima recompensa.",
  },
  de: {
    title: "Sammle Punkte bei jedem Einkauf",
    description: "Erhalte Punkte, wann immer du bei uns einkaufst, online oder im Geschäft.",
    longDescription: "Jede Bestellung bringt dir Punkte, egal ob online oder im Geschäft. Je mehr du einkaufst, desto schneller erreichst du deine nächste Belohnung.",
  },
  fr: {
    title: "Gagnez des points à chaque achat",
    description: "Recevez des points chaque fois que vous achetez chez nous, en ligne ou en boutique.",
    longDescription: "Chaque commande vous rapporte des points, que vous achetiez en ligne ou en boutique. Plus vous achetez, plus vite vous atteignez votre prochaine récompense.",
  },
  it: {
    title: "Guadagna punti a ogni acquisto",
    description: "Ottieni punti ogni volta che acquisti da noi, online o in negozio.",
    longDescription: "Ogni ordine ti fa guadagnare punti, sia online sia in negozio. Più acquisti, più velocemente raggiungi il tuo prossimo premio.",
  },
  bg: {
    title: "Печелете точки при всяка покупка",
    description: "Получавайте точки всеки път, когато пазарувате при нас, онлайн или в магазина.",
    longDescription: "Всяка поръчка ви носи точки, независимо дали пазарувате онлайн или в магазина. Колкото повече пазарувате, толкова по-бързо достигате до следващата си награда.",
  },
  ca: {
    title: "Guanya punts amb cada compra",
    description: "Aconsegueix punts cada vegada que compres amb nosaltres, en línia o a la botiga.",
    longDescription: "Cada comanda et dona punts, tant si compres en línia com a la botiga. Com més compres, més ràpid arribaràs a la teva propera recompensa.",
  },
  hu: {
    title: "Gyűjts pontokat minden vásárlás után",
    description: "Pontokat kapsz minden alkalommal, amikor nálunk vásárolsz, online vagy az üzletben.",
    longDescription: "Minden rendelésért pontokat kapsz, akár online, akár az üzletben vásárolsz. Minél többet vásárolsz, annál gyorsabban éred el a következő jutalmadat.",
  },
};

const LOCATION_CAMPAIGN_COPY: Record<OnboardingCustomerLocale, OnboardingLocationCampaignCopy> = {
  en: {
    name: "Onboarding Campaign",
    body: "Show your loyalty card to collect points",
  },
  es: {
    name: "Campaña de bienvenida",
    body: "Muestra tu tarjeta de fidelidad para acumular puntos",
  },
  de: {
    name: "Einsteigerkampagne",
    body: "Zeige deine Kundenkarte, um Punkte zu sammeln",
  },
  fr: {
    name: "Campagne de bienvenue",
    body: "Présentez votre carte de fidélité pour cumuler des points",
  },
  it: {
    name: "Campagna di benvenuto",
    body: "Mostra la tua carta fedeltà per accumulare punti",
  },
  bg: {
    name: "Кампания за добре дошли",
    body: "Покажете своята карта за лоялност, за да събирате точки",
  },
  ca: {
    name: "Campanya de benvinguda",
    body: "Mostra la teva targeta de fidelització per acumular punts",
  },
  hu: {
    name: "Üdvözlő kampány",
    body: "Mutasd fel hűségkártyádat a pontgyűjtéshez",
  },
};

export function getOnboardingProgramLocales(program: Pick<Program, "defaultLanguage" | "enabledLanguages">): OnboardingProgramLocales {
  const defaultLocale = normalizeOnboardingLocale(program.defaultLanguage);
  const enabledLocales = (program.enabledLanguages?.length ? program.enabledLanguages : ["en"])
    .map(locale => normalizeOnboardingLocale(locale))
    .filter((locale, index, locales) => locales.indexOf(locale) === index);

  return {
    defaultLocale,
    enabledLocales: enabledLocales.length ? enabledLocales : ["en"],
  };
}

export function getOnboardingRewardCopy(input: OnboardingRewardCopyInput): OnboardingRewardCopy {
  const locale = normalizeOnboardingLocale(input.locale);
  const value = formatCurrency(input.value, input.currency, locale);
  const redeemCost = new Intl.NumberFormat(locale).format(input.redeemCost);

  return {
    title: REWARD_TITLES[locale][input.nameKey],
    description: getRewardDescription(locale, value, redeemCost),
  };
}

export function getOnboardingEarningFlowCopy(locale: string): OnboardingEarningFlowCopy {
  return EARNING_FLOW_COPY[normalizeOnboardingLocale(locale)];
}

export function getOnboardingLocationCampaignCopy(locale: string): OnboardingLocationCampaignCopy {
  return LOCATION_CAMPAIGN_COPY[normalizeOnboardingLocale(locale)];
}

export function buildOnboardingTranslationRows(input: CreatedOnboardingEntitiesForTranslations): UpsertTranslationDto[] {
  const { enabledLocales } = getOnboardingProgramLocales(input.program);
  const translations: UpsertTranslationDto[] = [];

  for (const locale of enabledLocales) {
    if (input.programEngagementFlowId) {
      const earningFlowCopy = getOnboardingEarningFlowCopy(locale);
      translations.push(
        createTranslation("earning_flow", input.programEngagementFlowId, "title", locale, earningFlowCopy.title),
        createTranslation("earning_flow", input.programEngagementFlowId, "description", locale, earningFlowCopy.description),
        createTranslation("earning_flow", input.programEngagementFlowId, "longDescription", locale, earningFlowCopy.longDescription),
      );
    }

    for (const reward of input.rewards) {
      const rewardCopy = getOnboardingRewardCopy({
        nameKey: reward.nameKey,
        value: reward.value,
        redeemCost: reward.redeemCost,
        currency: input.program.currency,
        locale,
      });
      translations.push(
        createTranslation("reward", reward.rewardId, "title", locale, rewardCopy.title),
        createTranslation("reward", reward.rewardId, "description", locale, rewardCopy.description),
        createTranslation("coupon", reward.couponId, "name", locale, rewardCopy.title),
      );
    }

    const locationCampaignCopy = getOnboardingLocationCampaignCopy(locale);
    for (const campaignId of input.campaignIds) {
      translations.push(createTranslation("campaign", campaignId, "body", locale, locationCampaignCopy.body));
    }
  }

  return translations;
}

export function normalizeOnboardingLocale(locale: string | null | undefined): OnboardingCustomerLocale {
  const normalized = locale?.toLowerCase().split("-")[0];
  const supported = SUPPORTED_ONBOARDING_CUSTOMER_LOCALES.find(customerLocale => customerLocale === normalized);
  // Unsupported Shopify locales use English copy until customer-copy tables cover them explicitly.
  return supported ?? "en";
}

function formatCurrency(value: number, currency: string, locale: OnboardingCustomerLocale): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(value);
}

function getRewardDescription(locale: OnboardingCustomerLocale, value: string, redeemCost: string): string {
  const descriptions: Record<OnboardingCustomerLocale, string> = {
    en: `Get a ${value} discount by spending ${redeemCost} points from your balance`,
    es: `Obtén un descuento de ${value} gastando ${redeemCost} puntos de tu saldo`,
    de: `Erhalte ${value} Rabatt, indem du ${redeemCost} Punkte aus deinem Guthaben einlöst`,
    fr: `Obtenez une remise de ${value} en utilisant ${redeemCost} points de votre solde`,
    it: `Ottieni uno sconto di ${value} usando ${redeemCost} punti dal tuo saldo`,
    bg: `Получете отстъпка от ${value}, като използвате ${redeemCost} точки от баланса си`,
    ca: `Aconsegueix un descompte de ${value} gastant ${redeemCost} punts del teu saldo`,
    hu: `Szerezz ${value} értékű kedvezményt ${redeemCost} pontod beváltásával`,
  };

  return descriptions[locale];
}

function createTranslation(
  entityType: string,
  entityId: string,
  field: string,
  locale: string,
  value: string,
): UpsertTranslationDto {
  return {
    entityType,
    entityId,
    field,
    locale,
    value,
  };
}
